import { computed, makeAutoObservable, observable } from "mobx";
import { createContext } from "react";
import {
  ROOT_NODE_ID,
  BLOCK_PREFIX,
  FIRST_OPTION_NUMBER_MULTIPLE_BLOCK,
  DEFAULT_STROKE_COLOR,
  DEFAULT_PLACEHOLDER_OUTGOING_MESSAGE,
  SEGMENT_ROOT_NODE_ID,
  SEGMENT_TRY_AGAIN_NODE_ID,
  SEGMENT_VALIDATION_NODE_ID,
  SegmentBlockTypes,
} from "src/config/constants";
import {
  getDocValidationSegment,
  getDocValidationWithFaceRecognitionSegment,
  getEmailSegment,
  getPhoneSegment,
} from "src/config/flow_segments";
import {
  CompareStrings,
  getOptionColorByPrevColor,
  getDimensions,
  GenerateID,
} from "src/utils/utils";
import {
  OrganizeFlow as OrganizeFlowUtil,
  RemoveNode as RemoveNodeUtil,
} from "src/utils/flow";
import { getOutgoers } from "react-flow-renderer";
import mixpanel from "mixpanel-browser";

export class State {
  // observable

  currentBlock: string;
  currentSegment: Flow;
  currentSegmentBlock: string;
  awaitingCallbackButtonResponse: boolean;
  messages: Message[] = [];
  flow: Flow = {
    inboundMessage: "",
    isDeployed: false,
    errorMessages: [],
    variables: new Map(),
    nodes: [],
    edges: [],
  };
  backupFlow: Flow = this.flow;
  currentColorIndex: number;
  simulationRunning: boolean;
  hasAlreadyStartedFlowBuilding: boolean;

  constructor() {
    this.currentBlock = ROOT_NODE_ID;
    this.currentSegmentBlock = "";
    this.awaitingCallbackButtonResponse = false;
    this.currentSegment = {
      nodes: [],
      edges: [],
    };
    this.currentColorIndex = 0;
    this.simulationRunning = false;
    this.hasAlreadyStartedFlowBuilding = false;

    makeAutoObservable(this, {
      flow: observable,
      flowElements: computed,
    });
  }

  // computed

  get flowElements(): ElementsReactFlow {
    let flowElementsCopy: ElementsReactFlow = [];

    [...this.flow.nodes, ...this.flow.edges].forEach((element) => {
      flowElementsCopy.push(JSON.parse(JSON.stringify(element)));
    });

    return flowElementsCopy;
  }

  get currentBlockNode(): Block | null {
    const currentBlockNode = this.flow.nodes.find(
      (node) => node.id === this.currentBlock
    );
    if (!currentBlockNode) {
      return null;
    }

    return currentBlockNode;
  }

  get currentSegmentBlockNode(): Block | null {
    const currentSegmentBlockNode = this.currentSegment.nodes.find(
      (node) => node.id === this.currentSegmentBlock
    );
    if (!currentSegmentBlockNode) {
      return null;
    }

    return currentSegmentBlockNode;
  }

  // action

  removeBlock(nodeId: string) {
    const removedNodeParentId = this.flow.edges.find(
      (edge) => edge.target === nodeId
    )?.source;
    this.backupFlow = JSON.parse(JSON.stringify(this.flow));
    this.flow = RemoveNodeUtil(this.flow, nodeId);
    if (removedNodeParentId) {
      const parentNode = this.flow.nodes.find(
        (node) => node.id === removedNodeParentId
      );
      if (parentNode?.type === "multiple") {
        if (
          !this.flow.edges.find((edge) => edge.source === removedNodeParentId)
        ) {
          this.addPlaceholder(removedNodeParentId);
        }
      } else {
        this.addPlaceholder(removedNodeParentId);
      }
    }

    // TODO: dont use setimeout
    setTimeout(() => {
      this.organizeFlow();
    }, 1);
  }

  restoreFlow() {
    this.flow = this.backupFlow;
    this.flow.variables = new Map(this.flow.variables || []);
  }

  organizeFlow() {
    this.updateDimensions();
    this.flow = OrganizeFlowUtil(this.flow);
  }

  updateDimensions() {
    const newNodes = this.flow.nodes.map((node) => {
      const elements = document.querySelectorAll(`[data-id="${node.id}"]`);
      if (!elements || elements.length === 0) {
        return node;
      }

      node.data.dimensions = getDimensions(elements[0] as HTMLDivElement);
      return node;
    });
    this.flow.nodes = newNodes;
  }

  sendBotMessageCurrentBlock(): void {
    this.currentSegmentBlock = "";
    if (!this.currentBlockNode || this.awaitingCallbackButtonResponse) {
      return;
    }

    if (this.currentBlockNode.type === "root") {
      return;
    }

    if (SegmentBlockTypes[this.currentBlockNode.type]) {
      this.moveToNextSegmentBlock("");
      return;
    }

    if (this.currentBlockNode.type === "background_check") {
      const botMessage: Message = {
        id: `${this.currentBlock}-bot`,
        senderType: "bot",
        text: "Puedes ver el resultado de tu consulta de antecedentes en: ",
        file: "/Truora_CHK5f6fe28df401ac5cf7c279d9122ea568.pdf",
        downloadName:
          "https://www.truora.com/pdf/CHK5f6fe28df401ac5cf7c279d9122ea568.pdf",
        fileType: "download",
        timestamp: new Date().getTime(),
      };
      this.messages.push(botMessage);
      return;
    }

    if (
      !this.currentBlockNode?.data?.outgoingMessage ||
      this.currentBlockNode?.data?.outgoingMessage === ""
    ) {
      const botMessage: Message = {
        id: `${this.currentBlock}-bot`,
        senderType: "bot",
        text: DEFAULT_PLACEHOLDER_OUTGOING_MESSAGE,
        timestamp: new Date().getTime(),
      };
      this.messages.push(botMessage);
      return;
    }

    if (this.currentBlockNode.type !== "multiple") {
      const botMessage: Message = {
        id: `${this.currentBlock}-bot`,
        senderType: "bot",
        text: this.currentBlockNode?.data?.outgoingMessage || "",
        timestamp: new Date().getTime(),
      };
      this.messages.push(botMessage);
      return;
    }

    let messageText = this.currentBlockNode?.data?.outgoingMessage || "";
    messageText += "\n\n";

    const targets = this.getTargets(this.currentBlockNode.id);
    if (!targets) {
      const botMessage: Message = {
        id: `${this.currentBlock}-bot`,
        senderType: "bot",
        text: messageText,
        timestamp: new Date().getTime(),
      };
      this.messages.push(botMessage);
      return;
    }

    targets.map((node) => {
      if (
        !node.data?.order ||
        !node.data?.incomingMessage ||
        node.data?.incomingMessage === ""
      ) {
        return;
      }
      messageText +=
        `${node.data?.order}) ${node.data?.incomingMessage}` + "\n";
    });

    const botMessage: Message = {
      id: `${this.currentBlock}-bot`,
      senderType: "bot",
      text: messageText,
      timestamp: new Date().getTime(),
    };
    this.messages.push(botMessage);
  }

  sendBotMessageCurrentSegmentBlock(): void {
    if (!this.currentSegmentBlockNode || this.awaitingCallbackButtonResponse) {
      return;
    }

    let botMessage: Message = {
      id: `${this.currentSegmentBlock}-bot`,
      senderType: "bot",
      text: DEFAULT_PLACEHOLDER_OUTGOING_MESSAGE,
      file: this.currentSegmentBlockNode?.data?.file,
      fileType: "image",
      timestamp: new Date().getTime(),
    };

    if (
      !this.currentSegmentBlockNode?.data?.outgoingMessage ||
      this.currentSegmentBlockNode?.data?.outgoingMessage === ""
    ) {
      botMessage.text = DEFAULT_PLACEHOLDER_OUTGOING_MESSAGE;
      this.messages.push(botMessage);
      return;
    }

    if (this.currentSegmentBlockNode?.id === SEGMENT_VALIDATION_NODE_ID) {
      botMessage.text =
        this.currentSegmentBlockNode?.data?.outgoingMessage || "";
      botMessage.callbackButtons = [
        {
          title: "Validación Exitosa",
          value: "success",
          callback: () => {
            const successPathNodeID = this.flow.edges.find((edge) => {
              if (
                edge.source === this.currentBlock &&
                edge.data.type === "success"
              ) {
                return edge.target;
              }
            })?.target;
            if (!successPathNodeID) {
              return;
            }

            this.currentBlock = successPathNodeID;
            this.awaitingCallbackButtonResponse = false;
            this.sendBotMessageCurrentBlock();
            this.highlightCurrentBlock();
          },
        },
        {
          title: "Validación Fallida",
          value: "failure",
          callback: () => {
            let failurePathNodeID = "";
            if (this.currentSegmentBlockNode?.data?.conditionPaths) {
              failurePathNodeID =
                this.currentSegmentBlockNode?.data?.conditionPaths.failure ||
                "";
            }
            this.currentSegmentBlock = failurePathNodeID;
            this.awaitingCallbackButtonResponse = false;
            this.sendBotMessageCurrentSegmentBlock();
          },
        },
      ];
      this.messages.push(botMessage);
      this.awaitingCallbackButtonResponse = true;
      return;
    }

    if (this.currentSegmentBlockNode.type !== "multiple") {
      botMessage.text =
        this.currentSegmentBlockNode?.data?.outgoingMessage || "";
      this.messages.push(botMessage);
      return;
    }

    let messageText = this.currentSegmentBlockNode?.data?.outgoingMessage || "";
    messageText += "\n\n";

    const targets = this.currentSegment.edges
      .filter((edge) => edge.source === this.currentSegmentBlockNode?.id)
      .map((edge) => edge.target);
    const targetsNodes = this.currentSegment.nodes.filter((node) =>
      targets.includes(node.id)
    );

    if (!targetsNodes) {
      botMessage.text = messageText;
      this.messages.push(botMessage);
      return;
    }

    targetsNodes.map((node) => {
      if (
        !node.data?.order ||
        !node.data?.incomingMessage ||
        node.data?.incomingMessage === ""
      ) {
        return;
      }
      messageText +=
        `${node.data?.order}) ${node.data?.incomingMessage}` + "\n";
    });

    botMessage.text = messageText;
    this.messages.push(botMessage);
  }

  resetBlocksHighlight() {
    const newNodes = this.flow.nodes.map((node) => {
      node.data.highlighted = false;
      return node;
    });
    this.flow.nodes = newNodes;
  }

  resetCurrentBlock() {
    this.currentSegmentBlock = "";
    this.awaitingCallbackButtonResponse = false;
    this.currentBlock = ROOT_NODE_ID;
  }

  setInitialHighlight() {
    // TODO: implement
  }

  highlightCurrentBlock() {
    const newNodes = this.flow.nodes.map((node) => {
      if (node.id !== this.currentBlock) {
        return node;
      }
      node.data.highlighted = true;
      return node;
    });
    this.flow.nodes = newNodes;
  }

  moveToNextOption(incomingMessage: string): void {
    const currentBlockTargets = this.flow.edges.map((edge) => {
      if (edge.source !== this.currentBlock) {
        return "";
      }

      return edge.target;
    });

    const nextNode = this.flow.nodes.find((node) => {
      if (
        !node.data?.incomingMessage ||
        !currentBlockTargets.includes(node.id)
      ) {
        return false;
      }

      if (
        CompareStrings(incomingMessage, node.data?.incomingMessage) ||
        CompareStrings(incomingMessage, `${node.data?.order}`)
      ) {
        return true;
      }

      return false;
    });
    if (!nextNode) {
      return;
    }

    this.currentBlock = nextNode.id;
    this.sendBotMessageCurrentBlock();
    this.highlightCurrentBlock();
  }

  setCurrentSegment() {
    switch (this.currentBlockNode?.type) {
      case "document_validation_segment": {
        if (this.currentBlockNode.data?.validationInput?.faceValidation) {
          this.currentSegment = getDocValidationWithFaceRecognitionSegment();
          break;
        }
        this.currentSegment = getDocValidationSegment();
        break;
      }
      case "email_validation_segment": {
        this.currentSegment = getEmailSegment();
        break;
      }
      case "phone_validation_segment": {
        this.currentSegment = getPhoneSegment();
        break;
      }
    }

    this.currentSegmentBlock = SEGMENT_ROOT_NODE_ID;
  }

  moveToNextSegmentBlock(incomingMessage: string) {
    if (this.currentSegmentBlock === "") {
      this.setCurrentSegment();
      this.sendBotMessageCurrentSegmentBlock();
      return;
    }

    if (this.currentSegmentBlockNode?.id === SEGMENT_TRY_AGAIN_NODE_ID) {
      if (
        CompareStrings(incomingMessage, "No") ||
        CompareStrings(incomingMessage, "2")
      ) {
        const failurePathNodeID = this.flow.edges.find((edge) => {
          if (
            edge.source === this.currentBlock &&
            edge.data.type === "failure"
          ) {
            return edge.target;
          }
        })?.target;
        if (!failurePathNodeID) {
          return;
        }

        this.currentBlock = failurePathNodeID;
        this.sendBotMessageCurrentBlock();
        this.highlightCurrentBlock();
        return;
      }

      this.currentSegmentBlock = SEGMENT_ROOT_NODE_ID;
      this.sendBotMessageCurrentSegmentBlock();
      return;
    }

    if (this.currentSegmentBlockNode?.type === "multiple") {
      const currentBlockTargets = this.currentSegment.edges.map((edge) => {
        if (edge.source !== this.currentSegmentBlock) {
          return "";
        }

        return edge.target;
      });

      const nextNode = this.currentSegment.nodes.find((node) => {
        if (
          !node.data?.incomingMessage ||
          !currentBlockTargets.includes(node.id)
        ) {
          return false;
        }

        if (
          CompareStrings(incomingMessage, node.data?.incomingMessage) ||
          CompareStrings(incomingMessage, `${node.data?.order}`)
        ) {
          return true;
        }

        return false;
      });
      if (!nextNode) {
        return;
      }

      this.currentSegmentBlock = nextNode.id;
      this.sendBotMessageCurrentSegmentBlock();
      return;
    }

    const nextEdge = this.currentSegment.edges.find(
      (edge) => edge.source === this.currentSegmentBlock
    );
    if (!nextEdge) {
      return;
    }
    const newCurrentBlock = nextEdge.target;

    this.currentSegmentBlock = newCurrentBlock;
    this.sendBotMessageCurrentSegmentBlock();
  }

  // TODO: [fix] move to the next block when there are
  // 2 consecutives simple blocks (already asked UX team about that UX issue. Left with low priority)
  moveToNextBlock(incomingMessage: string): void {
    if (!this.currentBlockNode) {
      return;
    }

    if (SegmentBlockTypes[this.currentBlockNode.type]) {
      this.moveToNextSegmentBlock(incomingMessage);
      return;
    }

    if (this.currentBlockNode?.type === "finish") {
      mixpanel.track("simulator_conversation_finished");
      return;
    }

    if (this.currentBlockNode?.type === "multiple") {
      this.moveToNextOption(incomingMessage);
      return;
    }

    const nextEdge = this.flow.edges.find(
      (edge) => edge.source === this.currentBlock
    );
    if (!nextEdge) {
      return;
    }
    const newCurrentBlock = nextEdge.target;

    this.currentBlock = newCurrentBlock;
    this.sendBotMessageCurrentBlock();
    this.highlightCurrentBlock();
  }

  replaceBlock(block: Block, id: string) {
    let positionX: number = 0;
    let positionY: number = 0;

    let i = this.flow.nodes.findIndex((oldBlock) => {
      if (oldBlock.id === id) {
        positionX = oldBlock.position.x;
        positionY = oldBlock.position.y;
        return true;
      }
    });

    block.position.x = positionX;
    block.position.y = positionY;

    this.flow.nodes[i] = block;
  }

  addPlaceholder(parentId: string) {
    const parent = this.getBlock(parentId);
    if (
      parent?.type !== "document_validation_segment" &&
      parent?.type !== "email_validation_segment" &&
      parent?.type !== "phone_validation_segment"
    ) {
      this.addBlock({
        parentId,
        type: "placeholder",
        outgoingMessage: "",
      });

      return;
    }

    const existingEdgeTypes = this.flow.edges.map((edge) => {
      if (edge.source !== parentId) {
        return;
      }

      return edge.data?.type;
    });

    if (!existingEdgeTypes?.includes("failure")) {
      this.addBlock({
        parentId,
        type: "placeholder",
        outgoingMessage: "",
        strokeColor: "#f95a59",
        edgeLabel: "Validación Fallida",
        edgeType: "failure",
      });
    }

    if (!existingEdgeTypes?.includes("success")) {
      this.addBlock({
        parentId,
        type: "placeholder",
        outgoingMessage: "",
        strokeColor: "#12d67d",
        edgeLabel: "Validación Exitosa",
        edgeType: "success",
      });
    }
  }

  addBlock({
    parentId,
    id,
    outgoingMessage,
    order,
    incomingMessage,
    type,
    strokeColor,
    edgeLabel,
    edgeType,
  }: {
    parentId: string;
    id?: string;
    outgoingMessage: string;
    order?: number;
    incomingMessage?: string;
    type: BlockType;
    strokeColor?: string;
    edgeLabel?: string;
    edgeType?: EdgeType;
  }): string | null {
    const parent = this.getBlock(parentId);
    strokeColor =
      strokeColor || parent?.data?.strokeColor || DEFAULT_STROKE_COLOR;
    // Don't allow new blocks from a finish block type
    if (parent?.type === "finish") {
      return null;
    }
    if (parent?.type === "multiple") {
      order = order ? order : this.getNextOptionOrder(parentId);
      strokeColor = this.getNextOptionColor(parentId);
    }

    const newId = id ? id : GenerateID(BLOCK_PREFIX);
    const newNode = {
      id: newId,
      data: {
        dimensions: { width: 0, height: 0 },
        outgoingMessage,
        order,
        incomingMessage: incomingMessage || "",
        strokeColor,
        variable: {
          id: GenerateID("variable-"), // TODO: remove this field, use the node id instead.
          key: "",
          type: "",
          value: "",
        },
      },
      position: { x: 0, y: 0 },
      connectable: false,
      targetPosition: "left" as PositionReactFlow,
      sourcePosition: "left" as PositionReactFlow,
      type,
    };
    this.flow.variables?.set(newNode.data.variable.id, newNode.data.variable);
    this.flow.nodes.push(newNode);

    if (newId !== ROOT_NODE_ID) {
      this.flow.edges.push({
        id: `${parentId}-${newId}`,
        source: parentId,
        target: newId,
        animated: false,
        type: "step",
        label: edgeLabel,
        data: {
          type: edgeType,
        },
        labelStyle: { fill: strokeColor, fontWeight: 700 },
        style: {
          stroke: strokeColor,
          strokeWidth: "9px",
        },
      });
    }

    // TODO: dont use setimeout
    setTimeout(() => {
      this.organizeFlow();
    }, 1);

    return newId;
  }

  getTargets(source: string): NodeReactFlow[] | null {
    const nodeSource = this.flow.nodes.find((node) => node.id === source);
    if (!nodeSource) {
      return null;
    }
    return getOutgoers(nodeSource, this.flowElements);
  }

  updateBlock(id: string, data: NodeData) {
    let oldBlockIndex = 0;
    const oldBlock = this.flow.nodes.find((node, index) => {
      if (node.id === id) {
        oldBlockIndex = index;
        return true;
      }
    });
    if (!oldBlock) {
      return null;
    }

    oldBlock.data = data;
    this.flow.nodes[oldBlockIndex] = oldBlock;

    // TODO: dont use setimeout
    setTimeout(() => {
      this.organizeFlow();
    }, 1);

    return this.flow.nodes[oldBlockIndex];
  }

  // TODO: dont organize the flow, keep the same order after changing the block type
  // TODO: the modal goes to 0,0. Keep the modal in the same place, maybe close it and open it again...
  changeBlockType(id: string, newBlockType: BlockType) {
    const nodeIndex = this.flow.nodes.findIndex((node) => node.id === id);
    if (!nodeIndex) {
      return null;
    }
    this.flow.edges.map((edge) => {
      if (edge.source !== id) {
        return;
      }

      this.flow = RemoveNodeUtil(this.flow, edge.target);
    });
    this.flow.nodes[nodeIndex].type = newBlockType;
    this.addPlaceholder(id);

    const nodeCount = this.getFlowNodeCount();

    if (nodeCount === 1 && !this.hasAlreadyStartedFlowBuilding) {
      this.hasAlreadyStartedFlowBuilding = true;

      mixpanel.track("building_flow_started");
    }

    // TODO: dont use setimeout
    setTimeout(() => {
      this.organizeFlow();
    }, 1);
  }

  getBlock(id: string) {
    return this.flow.nodes.find((node) => node.id === id);
  }

  getNextOptionColor(parentId: string): string {
    const connections = this.flow.edges.map((edge) => {
      if (edge.source === parentId) {
        return edge.target;
      }
    });

    const lastOptionNode = this.flow.nodes
      .map((node) => {
        if (!connections.includes(node.id)) {
          return;
        }

        return node;
      })
      .reduce(function (prev, current) {
        return (prev?.data?.order || -1) > (current?.data?.order || -1)
          ? prev
          : current;
      });

    return getOptionColorByPrevColor(lastOptionNode?.data?.strokeColor || "");
  }

  getNextOptionOrder(parentId: string): number {
    const targetNodes = this.getTargets(parentId);
    if (!targetNodes || targetNodes.length === 0) {
      return FIRST_OPTION_NUMBER_MULTIPLE_BLOCK;
    }

    let lastOptionNumber = FIRST_OPTION_NUMBER_MULTIPLE_BLOCK;
    targetNodes.forEach((node) => {
      lastOptionNumber = Math.max(lastOptionNumber, node.data?.order);
    });

    return lastOptionNumber + 1;
  }

  changeErrorMessage(id: string, newMessage: string) {
    this.flow.errorMessages = this.flow.errorMessages?.map((errorMessage) => {
      if (errorMessage.id !== id) {
        return errorMessage;
      }
      errorMessage.message = newMessage;
      return errorMessage;
    });
  }

  removeErrorMessage(id: string) {
    this.flow.errorMessages = this.flow.errorMessages?.filter(
      (errorMessage) => {
        if (errorMessage.id === id) {
          return false;
        }

        return true;
      }
    );
  }

  addErrorMessage(newMessage: string) {
    const id = GenerateID("error-msg-");
    this.flow.errorMessages?.push({
      id,
      message: newMessage,
    });
  }

  getPrevDefinedVariables(currentNodeID: string) {
    let allParents: string[] = [];

    let parents = this.flow.edges.map((edge) => {
      if (edge.target === currentNodeID) {
        return edge.source;
      }
      return "";
    });

    allParents = (parents as string[]) || [];

    while (true) {
      let isRoot = true;
      const grandParents = this.flow.edges.map((edge) => {
        if (parents.includes(edge.target)) {
          // TODO: this doesn't work with a cyclid graph
          isRoot = false;
          return edge.source;
        }

        return "";
      });

      if (isRoot) {
        break;
      }

      parents = grandParents;
      allParents = allParents.concat(
        grandParents.slice(0, grandParents.length)
      );
    }

    const variables = this.flow.nodes.map((node) => {
      if (allParents.includes(node.id)) {
        return node.data.variable;
      }
    });

    return variables.filter((variable) => variable && variable.key !== "");
  }

  getFlowNodeCount(): number {
    let placeholderNodesInFlow = 0;

    Object.values(this.flow.nodes).forEach((node) => {
      if (node.type === "placeholder") {
        placeholderNodesInFlow += 1;
      }
    });

    const rootNodesInFlow = 1;

    return (
      Object.keys(this.flow.nodes).length -
      (placeholderNodesInFlow + rootNodesInFlow)
    );
  }

  getFlowNodeCountPerType(): Record<BlockType, number> {
    const nodeCount: Record<BlockType, number> = {
      background_check: 0,
      document_validation_segment: 0,
      email_validation_segment: 0,
      finish: 0,
      multiple: 0,
      phone_validation_segment: 0,
      placeholder: 0,
      recursive: 0,
      root: 0,
      simple: 0,
    };

    Object.values(this.flow.nodes).forEach((node) => {
      nodeCount[node.type]++;
    });

    return nodeCount;
  }
}

export const initialState = new State();
export const StateContext = createContext(initialState);
export type StoreProps = { store: State };
